import path from 'path-browserify';
import _ from 'underscore';
import { generateGuid, get } from '@bingads-webui-universal/primitive-utilities';
import { MIN_COUNT } from './constants';
import {
  IFileEntry,
  IGenerateFileParams,
  IFileValidationStatusParams,
  IFileValidationStatus,
  IIsRequiredValidParams,
  IGetStringParams,
} from './types';

export const removeDuplication = (newFileName: string, currentFileNameList: string[]): string => {
  let nameIndex = 0;
  let retValue = newFileName;
  const fileExtname = path.extname(newFileName);
  const fileBasename = path.basename(newFileName, fileExtname);

  while (currentFileNameList.indexOf(retValue) !== -1) {
    nameIndex += 1;
    retValue = `${fileBasename}(${nameIndex})${fileExtname}`;
  }

  return retValue;
};

const isExtnameValid = (fileName: string, allowedFileExtensions: string[]): boolean => {
  if (_.isEmpty(allowedFileExtensions)) {
    return true;
  }

  return _.indexOf(allowedFileExtensions, path.extname(fileName).toLowerCase()) !== -1;
};

export const getFileValidationStatus = ({
  file,
  allowedFileExtensions = [],
  validateFileExtensionsFailedMessage,
  customValidator = _.noop as () => undefined,
}: IFileValidationStatusParams): IFileValidationStatus => {
  const errorMessages: string[] = [];

  if (file) {
    if (!isExtnameValid(file.name, allowedFileExtensions)) {
      errorMessages.push(validateFileExtensionsFailedMessage);
    }

    const customValidateResult = customValidator(file);
    if (!_.isEmpty(customValidateResult)) {
      errorMessages.push(customValidateResult);
    }
  }

  return { isValid: errorMessages.length === 0, errorMessages };
};

export const generateFile = ({
  id = generateGuid(),
  file = null,
  isValid = false,
  shouldAutoRename = false,
  errorMessages = [],
  fileList = [],
}: IGenerateFileParams = {}): IFileEntry => ({
  id,
  displayName: shouldAutoRename ? removeDuplication(file.name, fileList.map(({ displayName }) => displayName)) : _.result(file, 'name', ''),
  file,
  errorMessages,
  isValid,
});

export const isListValid = (list: Array<{ isValid?: boolean }>): boolean => _.every(list, ({
  isValid
}) => isValid);

export const isRequiredValid = <T>({ list, required }: IIsRequiredValidParams<T>): boolean => {
  if (required) {
    return list ? list.length >= MIN_COUNT : false;
  }

  return true;
};

export const getFileListIdentifier = (fileList: Array<{ id: string }> | null): string => JSON.stringify(_.pluck(fileList, 'id'));

export const getString = ({ customStrings, i18n, key }: IGetStringParams): string => {
  const customString = get(customStrings, key);
  return _.isString(customString) ? customString : i18n.getString(key);
};
